import React from 'react';
import { Alert, Box, Button, Divider, Typography } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import StorageUsageBanner from './StorageUsageBanner';
import ProjectSummaryCard from './ProjectSummaryCard';
import UploadProgressList from './UploadProgressList';
import ProjectSelectionMenu from './ProjectSelectionMenu';
import ProjectListSort from './ProjectListSort';
import {
  SelectedProjectSummary,
  StorageUsageState,
  SubscriptionInfo,
  ProjectSettingsMap,
} from '../../../../models/batchProjectUpload';
import { DetectedProject } from '../../../../utils/projectDetection';
import { PaginationOptions } from '../../../../models/pagination';
import { useIsBrowser } from '@/context/ElectronContext';

interface SelectedProjectData {
  folderPath: string;
  projectName: string;
  totalSize: number;
}

interface StickyHeaderProps {
  subscription: SubscriptionInfo | null;
  storageUsage: StorageUsageState;
  summary: SelectedProjectSummary;
  selectedProjectsCount: number;
  totalDetectedProjects: number;
  selectedProjects?: SelectedProjectData[];
  detectedProjects: DetectedProject[];
  projectSettings: ProjectSettingsMap;
  selectedProjectsSet: Set<string>;
  toggleAll: () => void;
  selectProjects: (folderPaths: string[]) => void;
  deselectProjects: (folderPaths: string[]) => void;
  areSelectedExpanded: boolean;
  onToggleExpandSelected: () => void;
  isUploading: boolean;
  uploadProgress: { [projectName: string]: number };
  onProjectClick?: (folderPath: string) => void;
  pagination: PaginationOptions;
  onSortFieldChange: (sortField: string) => void;
  onSortDirectionToggle: () => void;
}

const StickyHeader: React.FC<StickyHeaderProps> = ({
  subscription,
  storageUsage,
  summary,
  selectedProjectsCount,
  totalDetectedProjects,
  selectedProjects = [],
  detectedProjects,
  projectSettings,
  selectedProjectsSet,
  toggleAll,
  selectProjects,
  deselectProjects,
  areSelectedExpanded,
  onToggleExpandSelected,
  isUploading,
  uploadProgress,
  onProjectClick,
  pagination,
  onSortFieldChange,
  onSortDirectionToggle,
}) => {
  const allSelected = selectedProjectsCount === totalDetectedProjects;
  const isBrowser = useIsBrowser();

  return (
    <Box
      sx={{
        top: 0,
        zIndex: 10,
        backgroundColor: 'background.paper',
        px: 2,
      }}
    >
      <StorageUsageBanner
        subscription={subscription}
        usage={storageUsage}
        totalSelectedSize={summary.totalSelectedSize}
        selectedProjects={selectedProjects}
        onProjectClick={onProjectClick}
      />

      {isBrowser && (
        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
          <Typography variant="caption" fontSize="0.75rem">
            <strong>Note:</strong> Your browser may have asked for confirmation
            to access this folder. This is a security feature when selecting
            folders with many files.
          </Typography>
        </Alert>
      )}

      <ProjectSummaryCard summary={summary} />

      <UploadProgressList
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />

      <Divider />

      <Box
        sx={{
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          '& > *': {
            height: '30.75px',
          },
        }}
      >
        <ProjectListSort
          pagination={pagination}
          onSortFieldChange={onSortFieldChange}
          onSortDirectionToggle={onSortDirectionToggle}
          isUploading={isUploading}
        />

        <Button
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={onToggleExpandSelected}
          disabled={isUploading || summary.selectedCount === 0}
          sx={{
            height: '30.75px',
            '& .MuiButton-startIcon': {
              marginRight: '8px',
            },
          }}
        >
          {areSelectedExpanded ? 'Collapse All' : 'Expand All'}
        </Button>

        <Box
          sx={{
            marginLeft: 'auto',
            height: '30.75px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ProjectSelectionMenu
            selectedProjects={selectedProjectsSet}
            detectedProjects={detectedProjects}
            projectSettings={projectSettings}
            toggleAll={toggleAll}
            selectProjects={selectProjects}
            deselectProjects={deselectProjects}
            allSelected={allSelected}
            isUploading={isUploading}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default StickyHeader;
